"use client";

import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Book02Icon,
  CheckmarkBadge03Icon,
  Coins01Icon,
  ChartLineData01Icon,
  Mortarboard02Icon,
  UserStar01Icon,
  DashboardSquare01Icon,
  GlobalSearchIcon,
  UserGroupIcon,
  ShieldUserIcon,
  MagicWandIcon,
} from "@hugeicons/core-free-icons";
import { cn } from "../../lib/utils";
import { HugeiconsIcon } from "@hugeicons/react";
import { Link } from "react-router-dom";

// Change Here
const FEATURES = [
    {
    id: "learn-build-earn",
    label: "Learn. Build. Earn.",
    icon: Book02Icon,
    image:
      "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?q=80&w=1200",
    description:
      "Real skills for where the world is actually going. You learn it, you build with it, you become who you are destined to be.",
    href: null,
  },
  {
    id: "verified-credentials",
    label: "Verified Credentials",
    icon: CheckmarkBadge03Icon,
        image:
      "https://pub-b365ff8e20e448c3a7257b35173d9a38.r2.dev/mockups%20nesolearn/f769fcf2-29d1-40a9-8f6e-92fd05e9bf26_removalai_preview.png",
    description:
      "Every certificate is locked to your name, permanent and instantly verifiable, no more wondering if it's real.",
    href: null,
  },
  {
    id: "earn-rewards",
    label: "Earn Rewards",
    icon: Coins01Icon,
        image:
      "https://pub-b365ff8e20e448c3a7257b35173d9a38.r2.dev/mockups%20nesolearn/logos/Dark%20Coin%20Design%201.png",
    description:
      "Finish a course, hit a milestone, get rewarded.",
    href: null,
  },
  {
    id: "track-growth",
    label: "Track Growth",
    icon: ChartLineData01Icon,
        image:
      "https://pub-b365ff8e20e448c3a7257b35173d9a38.r2.dev/mockups%20nesolearn/60c049ba-cb39-4d6b-9845-3f1e4fce34d9_removalai_preview.png",
    description:
      "See exactly what you know, what's next, and where you're behind, with recommendations built around you.",
    href: "/user-dashboard",
  },
  {
    id: "educator-upskilling",
    label: "Educator Upskilling",
    icon: Mortarboard02Icon,
    image:
      "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?q=80&w=1200",
    description:
      "Real training and capacity building for educators, not just a place to upload a course and hope.",
    href: null,
  },
  {
    id: "teach-own-earn",
    label: "Teach & Earn",
    icon: UserStar01Icon,
    image:
      "https://images.unsplash.com/photo-1571260899304-425eee4c7efc?q=80&w=1200",
    description:
      "Build a course once and keep it forever. Every time it helps someone, you get paid for it.",
    href: null,
  },
  {
    id: "for-institutions",
    label: "For Institutions",
    icon: DashboardSquare01Icon,
        image:
      "https://pub-b365ff8e20e448c3a7257b35173d9a38.r2.dev/mockups%20nesolearn/8293649e-ca48-4a44-b32c-7838577835c9_removalai_preview.png",
    description:
      "Real-time visibility into how people are learning, where they're stuck, and where to invest next.",
    href: "/for-institutions",
  },
  {
    id: "global-reach",
    label: "Global Reach",
    icon: GlobalSearchIcon,
    image:
      "https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=1200",
    description:
      "One identity, access to opportunities, globally recognized.",
    href: null,
  },
  {
    id: "community-mentorship",
    label: "Visioneer",
    icon: UserGroupIcon,
    image:
      "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?q=80&w=1200",
    description:
      "Mentors, peers, and builders who've actually done the thing you're trying to learn.",
    href: "/visioneer",
  },
  {
    id: "bank-grade-security",
    label: "Secure Credentials",
    icon: ShieldUserIcon,
        image:
      "https://pub-b365ff8e20e448c3a7257b35173d9a38.r2.dev/mockups%20nesolearn/68c7281f-e16d-4e64-b8b1-31f5d7cdba7e_removalai_preview.png",
    description:
      "Your identity, your credentials, your data. Nobody touches any of it but you.",
    href: null,
  },
  {
    id: "personalized-ai",
    label: "Adaptive AI",
    icon: MagicWandIcon,
        image:
      "https://pub-b365ff8e20e448c3a7257b35173d9a38.r2.dev/mockups%20nesolearn/9c20806a-e7a2-49ce-8c54-d2e12e430cf2_removalai_preview.png",
    description:
      "An AI that actually pays attention to you as a learner, or your team, and adjusts instead of assuming everyone's the same.",
    href: "/courses/ai-blockchain",
  },
];

const AUTO_PLAY_INTERVAL = 3000;
const ITEM_HEIGHT = 65;

const wrap = (min, max, v) => {
  const rangeSize = max - min;
  return ((((v - min) % rangeSize) + rangeSize) % rangeSize) + min;
};

export function FeatureCarousel() {
  const [step, setStep] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const currentIndex =
    ((step % FEATURES.length) + FEATURES.length) % FEATURES.length;

  const nextStep = useCallback(() => {
    setStep((prev) => prev + 1);
  }, []);

    const handleChipClick = (index) => {
    const diff = (index - currentIndex + FEATURES.length) % FEATURES.length;
    if (diff > 0) setStep((s) => s + diff);
  };

  const handleDragEnd = (_, info) => {
    const swipeThreshold = 50;
    if (info.offset.x < -swipeThreshold) {
      setStep((s) => s + 1);
    } else if (info.offset.x > swipeThreshold) {
      setStep((s) => s - 1);
    }
    setIsPaused(false);
  };

  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(nextStep, AUTO_PLAY_INTERVAL);
    return () => clearInterval(interval);
  }, [nextStep, isPaused]);

  const getCardStatus = (index) => {
    const diff = index - currentIndex;
    const len = FEATURES.length;

    let normalizedDiff = diff;
    if (diff > len / 2) normalizedDiff -= len;
    if (diff < -len / 2) normalizedDiff += len;

    if (normalizedDiff === 0) return "active";
    if (normalizedDiff === -1) return "prev";
    if (normalizedDiff === 1) return "next";
    return "hidden";
  };

  return (
    <div className="w-full max-w-7xl mx-auto md:p-8">
      <div className="relative overflow-hidden rounded-[2.5rem] lg:rounded-[4rem] flex flex-col lg:flex-row min-h-[600px] lg:aspect-video border border-border/40">
                        <div className="w-full lg:w-[40%] min-h-[350px] md:min-h-[450px] lg:h-full relative z-30 flex flex-col items-start justify-center overflow-hidden px-8 md:px-16 lg:pl-16 bg-gradient-to-br from-[#2a2a2a] via-[#1b1b1b] to-[#0d0d0d]">
          <div className="absolute inset-x-0 top-0 h-12 md:h-20 lg:h-16 bg-gradient-to-b from-[#1b1b1b] via-[#1b1b1b]/80 to-transparent z-40" />
          <div className="absolute inset-x-0 bottom-0 h-12 md:h-20 lg:h-16 bg-gradient-to-t from-[#0d0d0d] via-[#0d0d0d]/80 to-transparent z-40" />
          <div className="relative w-full h-full flex items-center justify-center lg:justify-start z-20">
            {FEATURES.map((feature, index) => {
              const isActive = index === currentIndex;
              const distance = index - currentIndex;
              const wrappedDistance = wrap(
                -(FEATURES.length / 2),
                FEATURES.length / 2,
                distance
              );

              return (
                <motion.div
                  key={feature.id}
                  style={{
                    height: ITEM_HEIGHT,
                    width: "fit-content",
                  }}
                  animate={{
                    y: wrappedDistance * ITEM_HEIGHT,
                    opacity: 1 - Math.abs(wrappedDistance) * 0.25,
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 90,
                    damping: 22,
                    mass: 1,
                  }}
                  className="absolute flex items-center justify-start"
                >
                  <button
                    onClick={() => handleChipClick(index)}
                    onMouseEnter={() => setIsPaused(true)}
                    onMouseLeave={() => setIsPaused(false)}
                    className={cn(
                      "relative flex items-center gap-4 px-6 md:px-10 lg:px-8 py-3.5 md:py-5 lg:py-4 rounded-full transition-all duration-700 text-left group border",
                                            isActive
                        ? "bg-white text-[#1b1b1b] border-white z-10"
                        : "bg-transparent text-white/60 border-white/20 hover:border-white/40 hover:text-white"
                    )}
                  >
                    <div
                      className={cn(
                        "flex items-center justify-center transition-colors duration-500",
                                                isActive ? "text-[#1b1b1b]" : "text-white/40"
                      )}
                    >
                      <HugeiconsIcon
                        icon={feature.icon}
                        size={18}
                        strokeWidth={2}
                      />
                    </div>

                    <span className="font-normal text-sm md:text-[15px] tracking-tight whitespace-nowrap uppercase">
                      {feature.label}
                    </span>
                  </button>
                </motion.div>
              );
            })}
          </div>
        </div>

                        <div className="flex-1 min-h-[500px] md:min-h-[600px] lg:h-full relative bg-white flex items-center justify-center py-16 md:py-24 lg:py-16 px-6 md:px-12 lg:px-10 overflow-hidden border-t lg:border-t-0 lg:border-l border-border/20">
          <motion.div
            className="relative w-full max-w-[420px] aspect-[4/5] flex items-center justify-center cursor-grab active:cursor-grabbing touch-pan-y"
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.2}
            onDragStart={() => setIsPaused(true)}
            onDragEnd={handleDragEnd}
          >
            {FEATURES.map((feature, index) => {
              const status = getCardStatus(index);
              const isActive = status === "active";
              const isPrev = status === "prev";
              const isNext = status === "next";

              return (
                <motion.div
                  key={feature.id}
                  initial={false}
                  animate={{
                    x: isActive ? 0 : isPrev ? -100 : isNext ? 100 : 0,
                    scale: isActive ? 1 : isPrev || isNext ? 0.85 : 0.7,
                    opacity: isActive ? 1 : isPrev || isNext ? 0.4 : 0,
                    rotate: isPrev ? -3 : isNext ? 3 : 0,
                    zIndex: isActive ? 20 : isPrev || isNext ? 10 : 0,
                    pointerEvents: isActive ? "auto" : "none",
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 260,
                    damping: 25,
                    mass: 0.8,
                  }}
                  className="absolute inset-0 rounded-[2rem] md:rounded-[2.8rem] overflow-hidden border-4 md:border-8 border-background bg-background origin-center"
                >
                  <img
                    src={feature.image}
                    alt={feature.label}
                    className={cn(
                      "w-full h-full object-cover transition-all duration-700",
                      isActive
                        ? "grayscale-0 blur-0"
                        : "grayscale blur-[2px] brightness-75"
                    )}
                  />

                  <AnimatePresence>
                    {isActive && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="absolute inset-x-0 bottom-0 p-10 pt-32 bg-gradient-to-t from-black/90 via-black/40 to-transparent flex flex-col justify-end pointer-events-none"
                      >
                                                {feature.href ? (
                          <Link
                            to={feature.href}
                            className="bg-background text-foreground px-4 py-1.5 rounded-full text-[11px] font-normal uppercase tracking-[0.2em] w-fit shadow-lg mb-3 border border-border/50 hover:bg-foreground hover:text-background transition-colors pointer-events-auto"
                          >
                            {feature.label}
                          </Link>
                        ) : (
                          <div className="bg-background text-foreground px-4 py-1.5 rounded-full text-[11px] font-normal uppercase tracking-[0.2em] w-fit shadow-lg mb-3 border border-border/50">
                            {feature.label}
                          </div>
                        )}
                        <p className="text-white font-normal text-xl md:text-2xl leading-tight drop-shadow-md tracking-tight">
                          {feature.description}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export default FeatureCarousel;